import { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Wind, Eye, MessageCircle, Play, Pause, SkipForward, Check, Trophy, Flame, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stepIcons = {
  breathing: Wind,
  visualization: Eye,
  self_talk: MessageCircle,
};

const stepColors = {
  breathing: { bg: "from-teal-500 to-emerald-600", ring: "ring-teal-400", text: "text-teal-100", accent: "text-teal-300" },
  visualization: { bg: "from-indigo-500 to-purple-600", ring: "ring-indigo-400", text: "text-indigo-100", accent: "text-indigo-300" },
  self_talk: { bg: "from-amber-500 to-orange-600", ring: "ring-amber-400", text: "text-amber-100", accent: "text-amber-300" },
};

function BreathingPacer({ active }) {
  const [phase, setPhase] = useState("inhale");
  const [count, setCount] = useState(4);

  useEffect(() => {
    if (!active) return;
    const phases = [
      { name: "inhale", dur: 4 },
      { name: "hold", dur: 4 },
      { name: "exhale", dur: 4 },
      { name: "hold", dur: 4 },
    ];
    let phaseIdx = 0;
    let c = phases[0].dur;

    const interval = setInterval(() => {
      c--;
      if (c <= 0) {
        phaseIdx = (phaseIdx + 1) % phases.length;
        c = phases[phaseIdx].dur;
      }
      setPhase(phases[phaseIdx].name);
      setCount(c);
    }, 1000);

    return () => clearInterval(interval);
  }, [active]);

  const scale = phase === "inhale" ? "scale-110" : phase === "exhale" ? "scale-90" : "scale-100";

  return (
    <div className="flex flex-col items-center my-6">
      <div className={`w-28 h-28 rounded-full bg-white/20 flex items-center justify-center transition-transform duration-1000 ease-in-out ${scale}`}>
        <div className="w-20 h-20 rounded-full bg-white/30 flex items-center justify-center">
          <span className="text-3xl font-bold text-white">{count}</span>
        </div>
      </div>
      <p className="text-white/90 text-lg font-medium mt-3 capitalize">{phase === "hold" ? "Hold" : phase}</p>
    </div>
  );
}

function AffirmationRotator({ affirmations, active }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!active || !affirmations?.length) return;
    const interval = setInterval(() => {
      setIdx((i) => (i + 1) % affirmations.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [active, affirmations]);

  if (!affirmations?.length) return null;

  return (
    <div className="my-6 text-center">
      <p className="text-2xl sm:text-3xl font-bold text-white leading-relaxed animate-fade-in" key={idx}>
        "{affirmations[idx]}"
      </p>
      <p className="text-white/50 text-sm mt-3">Repeat slowly. Feel it.</p>
    </div>
  );
}

export default function MVPSessionPlayer({ session, onComplete, onClose }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const timerRef = useRef(null);

  const step = session?.steps?.[currentStep];
  const totalSteps = session?.steps?.length || 0;
  const colors = stepColors[step?.type] || stepColors.breathing;
  const StepIcon = stepIcons[step?.type] || Wind;

  useEffect(() => {
    if (step) setTimeLeft(step.duration_seconds);
  }, [currentStep, step]);

  useEffect(() => {
    if (!isPlaying || timeLeft <= 0) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          // Auto-advance
          if (currentStep < totalSteps - 1) {
            setTimeout(() => {
              setCurrentStep((s) => s + 1);
            }, 500);
          } else {
            setIsCompleted(true);
            setIsPlaying(false);
            handleComplete();
          }
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [isPlaying, timeLeft, currentStep, totalSteps]);

  const handleComplete = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/mvp-session/${session.id}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (onComplete) onComplete();
    } catch (e) {
      console.error("Failed to mark session complete", e);
    }
  }, [session, onComplete]);

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const progressPct = step ? ((step.duration_seconds - timeLeft) / step.duration_seconds) * 100 : 0;

  if (!session || !step) return null;

  if (isCompleted) {
    return (
      <div className="fixed inset-0 z-50 bg-gradient-to-br from-emerald-600 to-teal-700 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-6">
            <Trophy className="w-12 h-12 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-2">Session Complete!</h2>
          <p className="text-white/80 mb-8">Great work focusing on {session.focus_areas?.join(" & ") || "your mental game"}</p>
          <Button data-testid="mvp-done-btn" onClick={onClose} className="bg-white text-emerald-700 hover:bg-white/90 px-8 py-3 text-lg">
            Done
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`fixed inset-0 z-50 bg-gradient-to-br ${colors.bg} flex flex-col`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <div className="flex items-center space-x-2">
          {session.steps.map((_, i) => (
            <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === currentStep ? "w-8 bg-white" : i < currentStep ? "w-4 bg-white/60" : "w-4 bg-white/20"}`} />
          ))}
        </div>
        <button data-testid="mvp-close-btn" onClick={onClose} className="text-white/70 hover:text-white p-1">
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Step content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
        <div className={`w-16 h-16 rounded-2xl bg-white/15 flex items-center justify-center mb-4 ${colors.ring} ring-2`}>
          <StepIcon className="w-8 h-8 text-white" />
        </div>
        <p className={`text-sm font-medium ${colors.accent} uppercase tracking-wider mb-1`}>
          Step {currentStep + 1} of {totalSteps}
        </p>
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">{step.title}</h2>

        {step.type === "breathing" && <BreathingPacer active={isPlaying} />}
        {step.type === "self_talk" && step.affirmations && (
          <AffirmationRotator affirmations={step.affirmations} active={isPlaying} />
        )}

        <p className={`text-base ${colors.text} max-w-md leading-relaxed ${step.type !== "breathing" && step.type !== "self_talk" ? "my-6" : ""}`}>
          {step.description}
        </p>

        {/* Timer */}
        <div className="mt-6 mb-4">
          <div className="w-64 h-2 bg-white/20 rounded-full overflow-hidden">
            <div className="h-full bg-white/70 rounded-full transition-all duration-1000" style={{ width: `${progressPct}%` }} />
          </div>
          <p className="text-white text-2xl font-mono mt-3">{formatTime(timeLeft)}</p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center space-x-6 pb-8 px-4">
        <Button
          data-testid="mvp-play-btn"
          onClick={() => setIsPlaying(!isPlaying)}
          className="w-16 h-16 rounded-full bg-white/20 hover:bg-white/30 border-2 border-white/40"
        >
          {isPlaying ? <Pause className="w-7 h-7 text-white" /> : <Play className="w-7 h-7 text-white ml-0.5" />}
        </Button>
        {currentStep < totalSteps - 1 && (
          <Button
            data-testid="mvp-skip-btn"
            onClick={() => { setCurrentStep(currentStep + 1); setIsPlaying(false); }}
            className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/30"
          >
            <SkipForward className="w-5 h-5 text-white" />
          </Button>
        )}
      </div>
    </div>
  );
}

export function MVPSessionCard({ onStart }) {
  const [streak, setStreak] = useState({ streak: 0, total_completed: 0 });
  const [todayComplete, setTodayComplete] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      const [streakRes, sessionRes] = await Promise.all([
        axios.get(`${API}/mvp-session/streak`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/mvp-session`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setStreak(streakRes.data);
      setTodayComplete(sessionRes.data?.completed || false);
    } catch (e) {
      // silent
    }
  };

  return (
    <Card
      data-testid="mvp-session-card"
      className="border-0 shadow-lg bg-gradient-to-br from-teal-600 to-emerald-700 text-white cursor-pointer hover:shadow-xl transition-all duration-300 active:scale-[0.98]"
      onClick={() => !todayComplete && onStart()}
    >
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1">
              <Wind className="w-5 h-5 text-teal-200" />
              <h3 className="font-bold text-base sm:text-lg">Daily Mental Warmup</h3>
            </div>
            <p className="text-teal-100 text-xs sm:text-sm">5-min breathing, visualization & self-talk</p>
            {streak.streak > 0 && (
              <div className="flex items-center space-x-1 mt-2">
                <Flame className="w-4 h-4 text-amber-300" />
                <span className="text-amber-200 text-xs font-semibold">{streak.streak} day streak</span>
              </div>
            )}
          </div>
          <div className="ml-3">
            {todayComplete ? (
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <Check className="w-6 h-6 text-emerald-200" />
              </div>
            ) : (
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30">
                <Play className="w-6 h-6 text-white ml-0.5" />
              </div>
            )}
          </div>
        </div>
        {todayComplete && <p className="text-emerald-200 text-xs mt-2 font-medium">Completed today</p>}
      </CardContent>
    </Card>
  );
}
